import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type ServerRow } from '../lib/api'
import { Spinner, Tag, ErrorNote, InfoNote } from '../components/ui/Primitives'
import { ConfirmDialog } from '../components/ui/ConfirmDialog'

interface Vhost {
  hostname: string
  upstreamPort: number | null
  configFile: string
  hasTls: boolean
  alreadyRegistered: boolean
}

export default function Servers() {
  const { data, isLoading } = useQuery({ queryKey: ['servers'], queryFn: () => api.get<ServerRow[]>('/servers') })
  if (isLoading) return <Spinner className="h-6 w-6" />

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-xl font-semibold">Servidores</h1>
        <p className="hint mt-0.5">{data?.length ?? 0} registrado(s)</p>
      </header>
      <div className="space-y-4">
        {(data ?? []).map((s) => <ServerCard key={s.id} server={s} />)}
      </div>
    </div>
  )
}

function ServerCard({ server }: { server: ServerRow }) {
  const qc = useQueryClient()
  const [showDiscover, setShowDiscover] = useState(false)
  const [confirmManage, setConfirmManage] = useState(false)

  const discover = useQuery({
    queryKey: ['discover', server.id],
    queryFn: () => api.get<Vhost[]>(`/servers/${server.id}/proxy/discover`),
    enabled: showDiscover,
  })

  const manage = useMutation({
    mutationFn: (confirmName: string) => api.post(`/servers/${server.id}/proxy/manage`, { managed: true, confirmName }),
    onSuccess: () => {
      setConfirmManage(false)
      void qc.invalidateQueries({ queryKey: ['servers'] })
    },
  })

  return (
    <div className="card space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-medium">{server.name}</p>
          <p className="hint mt-0.5">{server.hostname}{server.publicIp && ` · ${server.publicIp}`}</p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {server.isLocal && <Tag tone="blue">local</Tag>}
          <Tag tone={server.status === 'ONLINE' ? 'blue' : 'slate'}>{server.status.toLowerCase()}</Tag>
          <Tag>{server._count?.deployments ?? 0} instancia(s)</Tag>
        </div>
      </div>

      <div className="rounded-lg border p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-sm font-medium">Proxy: {server.proxyDriver.toLowerCase()}</p>
            <p className="hint mt-0.5">
              {server.proxyManaged
                ? 'El panel escribe la configuración de este proxy.'
                : 'El panel solo lo observa. No escribe nada.'}
            </p>
          </div>
          <div className="flex gap-2">
            <button className="btn-ghost text-sm" onClick={() => setShowDiscover((v) => !v)}>
              {showDiscover ? 'Ocultar' : 'Ver qué está sirviendo'}
            </button>
            {!server.proxyManaged && server.proxyDriver !== 'NONE' && (
              <button className="btn-ghost text-sm" onClick={() => setConfirmManage(true)}>
                Dejar que el panel lo gestione
              </button>
            )}
          </div>
        </div>

        {showDiscover && (
          <div className="mt-4">
            {discover.isLoading && <Spinner />}
            {discover.error && <ErrorNote>No se pudo leer la configuración del proxy.</ErrorNote>}
            {discover.data && discover.data.length === 0 && (
              <p className="hint">No se encontró ningún sitio configurado.</p>
            )}
            {discover.data && discover.data.length > 0 && (
              <div className="divide-y divide-slate-800/60 rounded-lg border">
                {discover.data.map((v) => (
                  <div key={v.hostname + v.configFile} className="flex flex-wrap items-center gap-3 px-4 py-2.5">
                    <code className="min-w-0 flex-1 truncate font-mono text-sm">{v.hostname}</code>
                    {v.upstreamPort && <span className="hint">:{v.upstreamPort}</span>}
                    {v.hasTls && <Tag tone="blue">TLS</Tag>}
                    {v.alreadyRegistered
                      ? <Tag tone="purple">en el panel</Tag>
                      : <Tag tone="slate">sin registrar</Tag>}
                  </div>
                ))}
              </div>
            )}
            <p className="hint mt-3">
              Esto es una lectura del servidor. No se modificó nada.
            </p>
          </div>
        )}
      </div>

      {server.proxyDriver === 'NGINX' && !server.proxyManaged && (
        <InfoNote>
          Este servidor usa nginx y probablemente ya esté sirviendo sitios en producción. Mientras
          el panel esté en solo lectura, no puede romper nada. Darle control es una decisión aparte
          que te va a pedir escribir el nombre del servidor.
        </InfoNote>
      )}

      <ConfirmDialog
        open={confirmManage}
        title="Dar control del proxy al panel"
        description={`A partir de acá, FersaPanel va a poder escribir archivos de configuración en el proxy de "${server.name}". Solo escribe archivos con su propio prefijo y valida con "nginx -t" antes de recargar, pero afecta a un servidor que ya está sirviendo tráfico.`}
        confirmName={server.name}
        confirmLabel="Dar control"
        busy={manage.isPending}
        onCancel={() => setConfirmManage(false)}
        onConfirm={(typed) => typed && manage.mutate(typed)}
      />
    </div>
  )
}
